import React from 'react';
import PropTypes from 'prop-types';
import { bem } from 'helpers/class-name';
import { intlMessage } from 'constants/propTypes';
import Message from 'components/Message';
import When from 'components/When';
import messages from '../ReservationFeedbackModalV2.messages';
import GuidancePanelContent from './GuidancePanelContent';
import ReportBadge from './ReportBadge';

import './GuidancePanel.scss';

const { block, element } = bem('guidance-panel');

const MobileGuidancePanel = ({
  reasonType,
  descriptionMessage,
  guidelines,
  isOpen,
  onToggle,
  venueId,
  reservationId,
}) => (
  <div className={block()}>
    <ReportBadge
      closeStateLabel={messages.guidancePanelClosedStateLabel}
      isExpanded={isOpen}
      isToggle
      onClick={onToggle}
      openStateLabel={messages.guidancePanelOpenStateLabel}
      text={reasonType}
    />
    <When predicate={isOpen}>
      <div id="guidance-panel-mobile" className={element('panel-mobile')}>
        <GuidancePanelContent
          descriptionMessage={descriptionMessage}
          guidelines={guidelines}
          venueId={venueId}
          reservationId={reservationId}
        />
      </div>
    </When>
  </div>
);

const DesktopGuidancePanel = ({ descriptionMessage, isTrialAbuse, guidelines, venueId, reservationId }) => {
  const titleMessage = isTrialAbuse ? messages.guidancePanelTrialAbuseTitle : messages.guidancePanelDisruptiveUserTitle;

  return (
    <div className={block()}>
      <div className={element('panel-header')}>
        <Message as="h1" className={element('panel-title')} message={titleMessage} />
      </div>
      <div className={element('panel-desktop')}>
        <GuidancePanelContent
          descriptionMessage={descriptionMessage}
          guidelines={guidelines}
          venueId={venueId}
          reservationId={reservationId}
        />
      </div>
    </div>
  );
};

MobileGuidancePanel.propTypes = {
  reasonType: intlMessage.isRequired,
  descriptionMessage: intlMessage.isRequired,
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.oneOfType([intlMessage, PropTypes.arrayOf(intlMessage)]).isRequired,
      descriptionFooter: intlMessage,
      icon: PropTypes.string.isRequired,
      title: intlMessage.isRequired,
    })
  ).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

DesktopGuidancePanel.propTypes = {
  descriptionMessage: intlMessage.isRequired,
  isTrialAbuse: PropTypes.bool.isRequired,
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.oneOfType([intlMessage, PropTypes.arrayOf(intlMessage)]).isRequired,
      descriptionFooter: intlMessage,
      icon: PropTypes.string.isRequired,
      title: intlMessage.isRequired,
    })
  ).isRequired,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const GuidancePanel = ({ reasonType, guidelines, isMobile, isOpen, onToggle, venueId, reservationId }) => {
  const isTrialAbuse = reasonType.id === messages.trialProgramAbuse.id;
  const descriptionMessage = isTrialAbuse
    ? messages.guidancePanelTrialAbuseDescription
    : messages.guidancePanelDisruptiveUserDescription;
  return isMobile ? (
    <MobileGuidancePanel
      reasonType={reasonType}
      descriptionMessage={descriptionMessage}
      guidelines={guidelines}
      isOpen={isOpen}
      onToggle={onToggle}
      venueId={venueId}
      reservationId={reservationId}
    />
  ) : (
    <DesktopGuidancePanel
      isTrialAbuse={isTrialAbuse}
      descriptionMessage={descriptionMessage}
      guidelines={guidelines}
      venueId={venueId}
      reservationId={reservationId}
    />
  );
};

GuidancePanel.propTypes = {
  reasonType: intlMessage.isRequired,
  guidelines: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.oneOfType([intlMessage, PropTypes.arrayOf(intlMessage)]).isRequired,
      descriptionFooter: intlMessage,
      icon: PropTypes.string.isRequired,
      title: intlMessage.isRequired,
    })
  ).isRequired,
  isMobile: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  venueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reservationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default GuidancePanel;
